import React, { useEffect, useState } from 'react';
import { ClickAwayListener } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';

import { Stack } from 'common/components/material';
import { useRouter, useView } from 'common/hooks';
import AppRoutesEnum from 'common/routes/AppRoutes.enum';
import { isInExplorePage } from 'common/utils/appBar.utils';
import {
  DEFAULT_PLACEHOLDER,
  SearchBarProps,
  SMALLER_SCREEN_PLACEHOLDER,
} from 'features/search';
import { SearchSuggestionType } from 'features/search/enums/search-suggestion-type.enum';
import { useAddRecentSearches } from 'features/search/hooks/useAddRecentSearches';
import { ISearchSuggestion } from 'features/search/interfaces/search-suggestion.interface';
import { IStaticDataSearchSuggestion } from 'features/static-data/interfaces/static-data-search-suggestion.interface';
import AppBarSearchInput from '../input/app-bar-search-input.component';
import SearchSuggestions from './search-suggestions';
import { useExploreTabs } from 'features/explore/hooks/useExploreTabs';
import { ExploreOrganizationTabsConfig } from 'features/org-root/config/explore-organization-tabs.config';

const SearchBar: React.FC<SearchBarProps> = ({
  onClose,
  placeholder,
  autoFocus,
  onFocus,
}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { goTo } = useRouter();
  const urlQuery = searchParams.get('query') || '';
  const { isDesktopView, isLargeScreenView } = useView();
  placeholder =
    placeholder || isLargeScreenView ? DEFAULT_PLACEHOLDER : SMALLER_SCREEN_PLACEHOLDER;
  const searchInputRef = React.useRef<HTMLInputElement>();

  /**
   * Here we are fetching allowed Explore tabs for the loggedin user,
   * once we have the allowed explore tabs, we select the first and let the user
   * route to that explore view.
   */
  const { exploreTabs } = useExploreTabs();
  const exploreTabRoute = isInExplorePage(location.pathname)
    ? location.pathname
    : (
        exploreTabs.find((v) => v.label === ExploreOrganizationTabsConfig.PEOPLE.label) ??
        exploreTabs[0] ??
        {}
      ).pathName;
  const explorePeopleTabRoute = exploreTabs.find(
    (v) => v.label === ExploreOrganizationTabsConfig.PEOPLE.label,
  )?.pathName;

  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchInputHeightOffset, setSearchInputHeightOffset] = useState(0);
  const [hasJustSelectedFilter, setHasJustSelectedFilter] = useState(false);

  const { submit: addRecentSearches } = useAddRecentSearches();

  useEffect(() => {
    setSearchText(urlQuery);
  }, [location.pathname, urlQuery, isDesktopView]);

  useEffect(() => {
    const searchInputHeight = searchInputRef?.current?.parentElement?.clientHeight || 40;
    setSearchInputHeightOffset(searchInputHeight);
  }, [isModalOpen]);

  useEffect(() => {
    if (hasJustSelectedFilter) {
      searchInputRef.current?.focus();
    }
  }, [hasJustSelectedFilter]);

  const searchKeyword = (searchText: string, pathname = exploreTabRoute) => {
    onClose && onClose();
    setIsModalOpen(false);
    if (!isInExplorePage(location.pathname, pathname)) {
      goTo(
        {
          pathname,
          search: {
            query: searchText,
          },
        },
        {},
      );
      return;
    }

    const searchParams2 = new URLSearchParams();
    searchParams2.set('query', searchText);
    setSearchParams(searchParams2, { replace: true });
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const onSearchInputChange = (value: string) => {
    openModal();
    setSearchText(value);
  };

  const handleFocus = () => {
    onFocus && onFocus();
    searchInputRef.current?.select();
    if (!hasJustSelectedFilter) {
      openModal();
    }
    setHasJustSelectedFilter(false);
  };

  const _onSuggestionSelection = (selectedSuggestion: ISearchSuggestion) => {
    const { id, type, data } = selectedSuggestion;

    const isStaticDataSuggestion = typeof data !== 'string' && 'isPrivate' in data;

    const StaticDataRoute =
      isStaticDataSuggestion && data?.isPrivate
        ? AppRoutesEnum.MANAGED_GROUP_OVERVIEW
        : AppRoutesEnum.STATIC_DATA_SUMMARY;

    addRecentSearches({
      value: type === SearchSuggestionType.TEXT ? data : id,
      type: type === SearchSuggestionType.PEOPLE_TEXT ? SearchSuggestionType.TEXT : type,
    });

    switch (selectedSuggestion?.type) {
      case SearchSuggestionType.STATIC_DATA:
        goTo(StaticDataRoute, {
          staticDataId: selectedSuggestion.id,
          staticDataType: (selectedSuggestion.data as IStaticDataSearchSuggestion)?.type,
        });
        break;
      case SearchSuggestionType.PROFILE:
        goTo(AppRoutesEnum.PROFILE, { profileId: selectedSuggestion?.id });
        break;
      case SearchSuggestionType.TEXT:
        searchKeyword(selectedSuggestion.data as string);
        break;
      case SearchSuggestionType.PEOPLE_TEXT:
        searchKeyword(selectedSuggestion.data as string, explorePeopleTabRoute);
        break;
    }
  };

  const onSelect = (selectedSuggestion: ISearchSuggestion) => {
    _onSuggestionSelection(selectedSuggestion);
    closeModal();
    onClose && onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' && !searchText && !isModalOpen) {
      openModal();
    }
  };

  const clearAll = () => {
    setSearchText('');
  };

  return (
    <ClickAwayListener mouseEvent={'onMouseDown'} onClickAway={closeModal}>
      <Stack direction="row" position={{ xs: 'static', md: 'relative' }} width="100%">
        <AppBarSearchInput
          autoComplete="one-time-code"
          id="global-search-bar"
          onFocus={handleFocus}
          autoFocus={autoFocus}
          onChange={onSearchInputChange}
          value={searchText}
          placeholder={placeholder}
          fullWidth={true}
          onClear={clearAll}
          inputRef={searchInputRef}
          onKeyDown={handleKeyDown}
          sx={{ '&.MuiTextField-root': { '.MuiInputBase-root': { minHeight: '40px' } } }}
        />

        {isModalOpen && (
          <SearchSuggestions
            searchTerm={searchText}
            topOffset={searchInputHeightOffset}
            onSelect={onSelect}
            onSubmit={() => {
              addRecentSearches({
                value: searchText,
                type: SearchSuggestionType.TEXT,
              });
              searchKeyword(searchText);
            }}
          />
        )}
      </Stack>
    </ClickAwayListener>
  );
};

export default SearchBar;
